import { useEffect, useState, useMemo, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../auth/AuthContext';
import usePlan from '../../hooks/usePlan';
import ReferralsTab from '../../components/admin/ReferralsTab';
import BillingTab from '../../components/admin/BillingTab';

function CopyButton({ text, style, label }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }, [text]);
  return (
    <button style={style} onClick={handleCopy}>
      {copied ? `\u2713 ${t('common.copied')}` : label}
    </button>
  );
}
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from 'recharts';

const API_BASE = import.meta.env.VITE_API_URL || `http://${window.location.hostname}:7070`;

const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'];

const styles = {
  main: { maxWidth: 720, margin: '0 auto', padding: '1.5rem 1rem' },
  tabs: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem', flexWrap: 'wrap' },
  tab: (active) => ({
    padding: '0.5rem 1rem', borderRadius: 8, border: 'none', cursor: 'pointer',
    fontWeight: 600, fontSize: '0.85rem',
    background: active ? '#7C3AED' : '#334155', color: active ? '#fff' : '#94A3B8',
  }),
  card: {
    background: '#1E293B', borderRadius: 12, padding: '1.5rem',
    boxShadow: '0 4px 24px rgba(0,0,0,0.25)', marginBottom: '1rem',
  },
  label: { display: 'block', fontSize: '0.85rem', fontWeight: 600, color: '#94A3B8', marginBottom: 4 },
  input: {
    padding: '0.5rem 0.6rem', borderRadius: 6, border: '1.5px solid #334155',
    fontSize: '0.9rem', outline: 'none', background: '#1E293B', color: '#F8FAFC',
  },
  inputFull: {
    width: '100%', padding: '0.5rem 0.6rem', borderRadius: 6, border: '1.5px solid #334155',
    fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem', background: '#1E293B', color: '#F8FAFC',
  },
  textarea: {
    width: '100%', padding: '0.5rem 0.6rem', borderRadius: 6, border: '1.5px solid #334155',
    fontSize: '0.9rem', outline: 'none', boxSizing: 'border-box', marginBottom: '0.75rem',
    resize: 'vertical', minHeight: 60, fontFamily: 'inherit', background: '#1E293B', color: '#F8FAFC',
  },
  btn: {
    padding: '0.65rem 1.25rem', borderRadius: 8, border: 'none',
    background: '#7C3AED', color: '#fff', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
  },
  btnOutline: {
    padding: '0.65rem 1.25rem', borderRadius: 8, border: '1.5px solid #334155',
    background: 'transparent', fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer', color: '#94A3B8',
  },
  btnDanger: {
    padding: '0.5rem 0.75rem', borderRadius: 6, border: 'none',
    background: '#FEE2E2', color: '#DC2626', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  },
  btnSmall: {
    padding: '0.4rem 0.75rem', borderRadius: 6, border: 'none',
    background: 'rgba(124,58,237,0.12)', color: '#7C3AED', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  },
  btnSmallSuccess: {
    padding: '0.4rem 0.75rem', borderRadius: 6, border: 'none',
    background: '#D1FAE5', color: '#059669', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  },
  btnWarning: {
    padding: '0.4rem 0.75rem', borderRadius: 6, border: 'none',
    background: '#FEF3C7', color: '#D97706', fontSize: '0.8rem', fontWeight: 600, cursor: 'pointer',
  },
  error: { color: '#EF4444', fontSize: '0.85rem', marginBottom: '0.75rem' },
  success: { color: '#10B981', fontSize: '0.85rem', marginBottom: '0.75rem' },
  sectionTitle: { fontWeight: 700, fontSize: '1rem', color: '#F8FAFC', marginBottom: '1rem' },
  dayRow: { display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap' },
  dayLabel: { width: 90, fontWeight: 600, fontSize: '0.85rem', color: '#F8FAFC' },
  closedLabel: { fontSize: '0.8rem', color: '#94A3B8', display: 'flex', alignItems: 'center', gap: 4 },
  qrContainer: { textAlign: 'center', padding: '1rem 0' },
  qrUrl: { fontSize: '0.85rem', color: '#94A3B8', wordBreak: 'break-all', marginTop: '1rem' },
  empRow: {
    display: 'flex', alignItems: 'center', padding: '0.75rem 0',
    borderBottom: '1px solid #334155', gap: '0.75rem',
  },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px', borderRadius: 4,
    fontSize: '0.7rem', fontWeight: 700, background: color, color: '#fff',
  }),
  select: {
    padding: '0.5rem 0.6rem', borderRadius: 6, border: '1.5px solid #334155',
    fontSize: '0.9rem', outline: 'none', background: '#1E293B', color: '#F8FAFC',
  },
  shopRow: {
    display: 'flex', alignItems: 'center', padding: '0.75rem 0',
    borderBottom: '1px solid #334155', gap: '0.75rem',
  },
  statCard: {
    background: '#1E293B', borderRadius: 12, padding: '1.25rem', textAlign: 'center',
    boxShadow: '0 2px 12px rgba(0,0,0,0.25)',
  },
  statValue: { fontSize: '2rem', fontWeight: 800, color: '#F8FAFC' },
  statLabel: { fontSize: '0.8rem', color: '#94A3B8', marginTop: 4 },
};

const roleBadgeColor = { OWNER: '#7C3AED', MANAGER: '#7C3AED', STAFF: '#10B981' };

// ── Shops Tab ─────────────────────────────────────────────────────────────────

const BROWSER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

function ShopsTab({ token, shops, onShopsChange }) {
  const { t } = useTranslation();
  const [form, setForm] = useState({ name: '', description: '', timezone: BROWSER_TZ, avgServiceMinutes: 10 });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const startEdit = (shop) => { setEditing(shop.id); setForm({ name: shop.name, description: shop.description || '', timezone: shop.timezone || BROWSER_TZ, avgServiceMinutes: shop.avgServiceMinutes ?? 10 }); setMsg(null); };
  const cancelEdit = () => { setEditing(null); setForm({ name: '', description: '', timezone: BROWSER_TZ, avgServiceMinutes: 10 }); setMsg(null); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    try {
      const isNew = !editing;
      const url = isNew ? `${API_BASE}/api/v1/shops` : `${API_BASE}/api/v1/shops/${editing}`;
      const body = { ...form, avgServiceMinutes: Number(form.avgServiceMinutes) || 10 };
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      if (!res.ok) {
        let errorCode = '';
        let serverMsg = '';
        try {
          const body = await res.json();
          errorCode = body?.error || '';
          serverMsg = body?.message || '';
        } catch { /* response not JSON */ }
        if (errorCode === 'QUOTA_EXCEEDED') {
          setMsg({ type: 'error', text: t('settings.shops.quotaExceeded') });
          return;
        }
        const base = t('settings.shops.failedSave');
        const detail = serverMsg || errorCode;
        setMsg({ type: 'error', text: detail ? `${base} (${res.status}): ${detail}` : `${base} (${res.status})` });
        return;
      }
      setMsg({ type: 'success', text: isNew ? t('settings.shops.created') : t('settings.shops.updated') });
      setForm({ name: '', description: '', timezone: BROWSER_TZ, avgServiceMinutes: 10 }); setEditing(null); onShopsChange();
    } catch { setMsg({ type: 'error', text: t('settings.networkError') }); } finally { setSaving(false); }
  };

  const toggleActive = async (shopId) => {
    await fetch(`${API_BASE}/api/v1/shops/${shopId}/toggle-active`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    onShopsChange();
  };

  return (
    <div>
      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('settings.shops.yourShops')}</div>
        {shops.length === 0 && <p style={{ color: '#94A3B8', fontSize: '0.85rem' }}>{t('settings.shops.noShops')}</p>}
        {shops.map((shop) => (
          <div key={shop.id} style={{ ...styles.shopRow, opacity: shop.active === false ? 0.5 : 1 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 8 }}>
                {shop.name}
                {shop.active === false && <span style={{ ...styles.badge('#EF4444'), fontSize: '0.65rem' }}>{t('settings.frozen')}</span>}
              </div>
              {shop.description && <div style={{ fontSize: '0.8rem', color: '#64748B', marginTop: 2 }}>{shop.description}</div>}
              <div style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: 2 }}>
                {shop.timezone || 'UTC'}
                {typeof shop.avgServiceMinutes === 'number' && ` · ${t('settings.shops.avgServiceTimeShort', { min: shop.avgServiceMinutes })}`}
              </div>
            </div>
            <button style={shop.active === false ? styles.btnSmallSuccess : styles.btnWarning} onClick={() => toggleActive(shop.id)}>
              {shop.active === false ? t('settings.unfreeze') : t('settings.freeze')}
            </button>
            <button style={styles.btnSmall} onClick={() => startEdit(shop)}>{t('settings.edit')}</button>
          </div>
        ))}
      </div>
      <div style={styles.card}>
        <div style={styles.sectionTitle}>{editing ? t('settings.shops.editShop') : t('settings.shops.addNewShop')}</div>
        {msg && <p style={msg.type === 'error' ? styles.error : styles.success}>{msg.text}</p>}
        <form onSubmit={save}>
          <label style={styles.label}>{t('settings.shops.name')}</label>
          <input style={styles.inputFull} placeholder={t('settings.shops.namePlaceholder')} required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          <label style={styles.label}>{t('settings.shops.description')}</label>
          <textarea style={styles.textarea} placeholder={t('settings.shops.descriptionPlaceholder')} value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
          <label style={styles.label}>{t('settings.shops.timezone')}</label>
          <select style={styles.select} value={form.timezone} onChange={(e) => setForm((f) => ({ ...f, timezone: e.target.value }))}>
            {Intl.supportedValuesOf('timeZone').map((tz) => (
              <option key={tz} value={tz}>{tz.replace(/_/g, ' ')}</option>
            ))}
          </select>
          <label style={{ ...styles.label, marginTop: '0.75rem' }}>{t('settings.shops.avgServiceTime')}</label>
          <input style={styles.inputFull} type="number" min="1" max="240" required value={form.avgServiceMinutes}
            onChange={(e) => setForm((f) => ({ ...f, avgServiceMinutes: e.target.value === '' ? '' : Number(e.target.value) }))} />
          <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '-0.5rem', marginBottom: '0.25rem' }}>{t('settings.shops.avgServiceTimeHint')}</p>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
            <button style={styles.btn} type="submit" disabled={saving}>{saving ? t('settings.saving') : editing ? t('settings.shops.updateShop') : t('settings.shops.createShop')}</button>
            {editing && <button style={styles.btnOutline} type="button" onClick={cancelEdit}>{t('settings.cancel')}</button>}
          </div>
        </form>
      </div>
    </div>
  );
}

// ── Services Tab ──────────────────────────────────────────────────────────────

function ServicesTab({ token, shops }) {
  const { t } = useTranslation();
  const [services, setServices] = useState([]);
  const defaultShopId = shops.length > 0 ? shops[0].id : '';
  const [form, setForm] = useState({ shopId: defaultShopId, name: '', description: '', avgServiceMinutes: '' });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [assignments, setAssignments] = useState([]);

  // Keep shopId synced when shops load
  useEffect(() => {
    if (!form.shopId && shops.length > 0) setForm(f => ({ ...f, shopId: shops[0].id }));
  }, [shops]);

  const loadData = () => {
    fetch(`${API_BASE}/api/v1/services`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(data => setServices(Array.isArray(data) ? data : [])).catch(() => {});
    fetch(`${API_BASE}/api/v1/employees`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(data => setEmployees(Array.isArray(data) ? data : [])).catch(() => {});
    fetch(`${API_BASE}/api/v1/services/assignments`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(data => setAssignments(Array.isArray(data) ? data : [])).catch(() => {});
  };

  useEffect(loadData, [token]);

  const save = async (e) => {
    e.preventDefault(); setSaving(true); setMsg(null);
    const shopId = editing ? form.shopId : (form.shopId || shops[0]?.id);
    if (!shopId) { setMsg({ type: 'error', text: t('settings.services.selectShopFirst') }); setSaving(false); return; }
    try {
      const isNew = !editing;
      const url = isNew ? `${API_BASE}/api/v1/services` : `${API_BASE}/api/v1/services/${editing}`;
      const body = { shopId, name: form.name, description: form.description, avgServiceMinutes: form.avgServiceMinutes === '' ? null : Number(form.avgServiceMinutes) };
      const res = await fetch(url, { method: isNew ? 'POST' : 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(body) });
      if (!res.ok) { const data = await res.json().catch(() => ({})); setMsg({ type: 'error', text: data.message || data.error || t('settings.services.failedSave') }); return; }
      setMsg({ type: 'success', text: isNew ? t('settings.services.created') : t('settings.services.updated') });
      setForm({ shopId: shops[0]?.id || '', name: '', description: '', avgServiceMinutes: '' }); setEditing(null); loadData();
    } catch { setMsg({ type: 'error', text: t('settings.networkError') }); } finally { setSaving(false); }
  };

  const deleteService = async (id) => {
    if (!confirm(t('settings.services.confirmDelete'))) return;
    await fetch(`${API_BASE}/api/v1/services/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    loadData();
  };

  const toggleAssignment = async (userId, serviceId) => {
    const exists = assignments.some(a => a.userId === userId && a.serviceId === serviceId);
    const url = `${API_BASE}/api/v1/services/${exists ? 'unassign' : 'assign'}`;
    await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ userId, serviceId }) }).catch(() => {});
    loadData();
  };

  const shopName = (shopId) => shops.find(s => s.id === shopId)?.name || '';

  return (
    <div>
      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('settings.services.title')}</div>
        {services.length === 0 && <p style={{ color: '#94A3B8', fontSize: '0.85rem' }}>{t('settings.services.noServices')}</p>}
        {services.map((svc) => (
          <div key={svc.id} style={styles.shopRow}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#F8FAFC' }}>{svc.name}</div>
              <div style={{ fontSize: '0.75rem', color: '#94A3B8' }}>{shopName(svc.shopId)}</div>
              {svc.description && <div style={{ fontSize: '0.8rem', color: '#64748B', marginTop: 2 }}>{svc.description}</div>}
              <div style={{ fontSize: '0.75rem', color: '#94A3B8', marginTop: 2 }}>
                {svc.avgServiceMinutes != null
                  ? t('settings.shops.avgServiceTimeShort', { min: svc.avgServiceMinutes })
                  : t('settings.services.serviceTimeInherits')}
              </div>
            </div>
            <button style={styles.btnSmall} onClick={() => { setEditing(svc.id); setForm({ shopId: svc.shopId, name: svc.name, description: svc.description || '', avgServiceMinutes: svc.avgServiceMinutes ?? '' }); }}>{t('settings.edit')}</button>
            <button style={styles.btnDanger} onClick={() => deleteService(svc.id)}>{t('settings.delete')}</button>
          </div>
        ))}
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>{editing ? t('settings.services.editService') : t('settings.services.addService')}</div>
        {msg && <p style={msg.type === 'error' ? styles.error : styles.success}>{msg.text}</p>}
        <form onSubmit={save}>
          {!editing && (
            <>
              <label style={styles.label}>{t('settings.services.shop')}</label>
              <select style={{ ...styles.select, width: '100%', marginBottom: '0.75rem' }} value={form.shopId} onChange={(e) => setForm(f => ({ ...f, shopId: e.target.value }))}>
                <option value="">{t('settings.services.selectShop')}</option>
                {shops.filter(s => s.active !== false).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </>
          )}
          <label style={styles.label}>{t('settings.services.name')}</label>
          <input style={styles.inputFull} placeholder={t('settings.services.namePlaceholder')} required value={form.name} onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))} />
          <label style={styles.label}>{t('settings.services.description')}</label>
          <textarea style={styles.textarea} placeholder={t('settings.services.descriptionPlaceholder')} value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} />
          <label style={styles.label}>{t('settings.services.serviceTime')}</label>
          <input style={styles.inputFull} type="number" min="1" max="240" placeholder={t('settings.services.serviceTimePlaceholder')} value={form.avgServiceMinutes} onChange={(e) => setForm(f => ({ ...f, avgServiceMinutes: e.target.value === '' ? '' : Number(e.target.value) }))} />
          <p style={{ fontSize: '0.75rem', color: '#64748B', marginTop: '-0.5rem', marginBottom: '0.75rem' }}>{t('settings.services.serviceTimeHint')}</p>
          <div style={{ display: 'flex', gap: '0.75rem' }}>
            <button style={styles.btn} type="submit" disabled={saving}>{saving ? t('settings.saving') : editing ? t('settings.update') : t('settings.services.createService')}</button>
            {editing && <button style={styles.btnOutline} type="button" onClick={() => { setEditing(null); setForm({ shopId: '', name: '', description: '', avgServiceMinutes: '' }); }}>{t('settings.cancel')}</button>}
          </div>
        </form>
      </div>

      {services.length > 0 && employees.length > 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>{t('settings.services.employeeAssignments')}</div>
          <p style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '1rem' }}>{t('settings.services.toggleServices')}</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #334155', color: '#94A3B8' }}>{t('settings.employee')}</th>
                  {services.map(svc => (
                    <th key={svc.id} style={{ textAlign: 'center', padding: '8px', borderBottom: '2px solid #334155', color: '#94A3B8', minWidth: 80 }}>{svc.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.filter(e => e.active !== false).map(emp => (
                  <tr key={emp.id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #334155' }}>
                      <span style={{ fontWeight: 600 }}>{emp.email}</span>
                      <span style={{ ...styles.badge(roleBadgeColor[emp.role]), marginLeft: 8 }}>{emp.role}</span>
                    </td>
                    {services.map(svc => {
                      const assigned = assignments.some(a => a.userId === emp.id && a.serviceId === svc.id);
                      return (
                        <td key={svc.id} style={{ textAlign: 'center', padding: '8px', borderBottom: '1px solid #334155' }}>
                          <input type="checkbox" checked={assigned} onChange={() => toggleAssignment(emp.id, svc.id)} style={{ cursor: 'pointer', width: 18, height: 18 }} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Business Hours Tab ────────────────────────────────────────────────────────

function BusinessHoursTab({ shopId, token, shopTimezone }) {
  const { t } = useTranslation();
  const [hours, setHours] = useState(DAY_KEYS.map((_, i) => ({ dayOfWeek: i, openTime: '09:00', closeTime: '17:00', closed: false })));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  useEffect(() => {
    if (!shopId) return;
    fetch(`${API_BASE}/api/v1/shops/${shopId}/business-hours`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json()).then((data) => { if (data.length > 0) setHours(data); }).catch(() => {});
  }, [shopId, token]);

  const update = (i, field, value) => setHours((h) => h.map((d, j) => (j === i ? { ...d, [field]: value } : d)));

  const save = async () => {
    setSaving(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/shops/${shopId}/business-hours`, { method: 'PUT', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ entries: hours }) });
      setMsg(res.ok ? { type: 'success', text: t('settings.saved') } : { type: 'error', text: t('settings.hours.failedSave') });
    } catch { setMsg({ type: 'error', text: t('settings.networkError') }); } finally { setSaving(false); }
  };

  return (
    <div style={styles.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
        <div style={styles.sectionTitle}>{t('settings.hours.title')}</div>
        <span style={{ fontSize: '0.8rem', color: '#94A3B8' }}>{shopTimezone || 'UTC'}</span>
      </div>
      {msg && <p style={msg.type === 'error' ? styles.error : styles.success}>{msg.text}</p>}
      {hours.map((d, i) => (
        <div key={i} style={styles.dayRow}>
          <span style={styles.dayLabel}>{t(`settings.days.${DAY_KEYS[i]}`)}</span>
          <input type="time" style={styles.input} value={d.openTime} onChange={(e) => update(i, 'openTime', e.target.value)} disabled={d.closed} />
          <span style={{ color: '#94A3B8' }}>-</span>
          <input type="time" style={styles.input} value={d.closeTime} onChange={(e) => update(i, 'closeTime', e.target.value)} disabled={d.closed} />
          <label style={styles.closedLabel}><input type="checkbox" checked={d.closed} onChange={(e) => update(i, 'closed', e.target.checked)} /> {t('settings.hours.closed')}</label>
        </div>
      ))}
      <button style={styles.btn} onClick={save} disabled={saving}>{saving ? t('settings.saving') : t('settings.hours.saveHours')}</button>
    </div>
  );
}

// ── QR Code Tab ───────────────────────────────────────────────────────────────

function QrCodeTab({ shopId, token, shops }) {
  const { t } = useTranslation();
  const [qrUrl, setQrUrl] = useState(null);
  const [services, setServices] = useState([]);
  const [serviceQrs, setServiceQrs] = useState({});
  const customerUrl = `${window.location.origin}/q/${shopId}`;
  const shop = shops?.find((s) => s.id === shopId);
  const isDynamicQr = !!shop?.dynamicQrEnabled;

  useEffect(() => {
    if (!shopId) return;
    fetch(`${API_BASE}/api/v1/shops/${shopId}/qr-code`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((blob) => setQrUrl(URL.createObjectURL(blob))).catch(() => {});
    fetch(`${API_BASE}/api/v1/services/by-shop/${shopId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(setServices).catch(() => {});
  }, [shopId, token]);

  useEffect(() => {
    if (!shopId || services.length === 0) return;
    services.forEach((svc) => {
      fetch(`${API_BASE}/api/v1/shops/${shopId}/qr-code/service/${svc.id}`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => r.blob()).then((blob) => setServiceQrs((prev) => ({ ...prev, [svc.id]: URL.createObjectURL(blob) }))).catch(() => {});
    });
  }, [shopId, services, token]);

  const download = (url, name) => { const a = document.createElement('a'); a.href = url; a.download = name; a.click(); };

  return (
    <div>
      {isDynamicQr && (
        <div style={{ ...styles.card, background: '#1E293B', borderColor: '#F59E0B' }}>
          <p style={{ margin: 0, color: '#F59E0B', fontSize: '0.9rem', fontWeight: 600 }}>
            {t('settings.qr.dynamicActive')}
          </p>
          <p style={{ margin: '0.5rem 0 0', color: '#94A3B8', fontSize: '0.85rem' }}>
            {t('settings.qr.dynamicActiveHint')}
          </p>
        </div>
      )}

      {!isDynamicQr && (
        <>
          {/* General QR code */}
          <div style={styles.card}>
            <div style={styles.sectionTitle}>{t('settings.qr.generalTitle')}</div>
            <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0 0 1rem' }}>
              {services.length > 1 ? t('settings.qr.chooseServiceAfterScan') : t('settings.qr.scanToJoin')}
            </p>
            <div style={styles.qrContainer}>
              {qrUrl ? <img src={qrUrl} alt={t('settings.qr.qrAlt')} style={{ width: 240, height: 240, borderRadius: 8 }} /> : <p style={{ color: '#94A3B8' }}>{t('settings.loading')}</p>}
              <p style={styles.qrUrl}>{customerUrl}</p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button style={styles.btn} onClick={() => download(qrUrl, `vqueue-qr-${shopId}.png`)}>{t('settings.qr.downloadPng')}</button>
              <CopyButton text={customerUrl} style={styles.btnOutline} label={t('settings.qr.copyUrl')} />
            </div>
          </div>

      {/* Per-service QR codes */}
      {services.length > 1 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>{t('settings.qr.serviceTitle')}</div>
          <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0 0 1rem' }}>
            {t('settings.qr.serviceDescription')}
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '1rem' }}>
            {services.map((svc) => {
              const svcUrl = `${customerUrl}?service=${svc.id}`;
              return (
                <div key={svc.id} style={{ textAlign: 'center', padding: '1rem', background: '#1E293B', borderRadius: 10 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.9rem', color: '#F8FAFC', marginBottom: 8 }}>{svc.name}</div>
                  {serviceQrs[svc.id]
                    ? <img src={serviceQrs[svc.id]} alt={t('settings.qr.altForService', { service: svc.name })} style={{ width: 160, height: 160, borderRadius: 6 }} />
                    : <p style={{ color: '#94A3B8', fontSize: '0.8rem' }}>{t('settings.loading')}</p>
                  }
                  <p style={{ fontSize: '0.7rem', color: '#94A3B8', wordBreak: 'break-all', marginTop: 6 }}>{svcUrl}</p>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 8 }}>
                    <button style={styles.btnSmall} onClick={() => download(serviceQrs[svc.id], `vqueue-qr-${svc.name.replace(/\s+/g, '-')}.png`)}>{t('settings.qr.download')}</button>
                    <CopyButton text={svcUrl} style={{ ...styles.btnSmall, background: '#334155', color: '#94A3B8' }} label={t('settings.qr.copy')} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
        </>
      )}
    </div>
  );
}

// ── Employees Tab ─────────────────────────────────────────────────────────────

function EmployeesTab({ token, currentUserId, currentUserRole }) {
  const { t } = useTranslation();
  const [employees, setEmployees] = useState([]);
  const [invites, setInvites] = useState([]);
  const [shops, setShops] = useState([]);
  const [shopAssignments, setShopAssignments] = useState([]);
  const [form, setForm] = useState({ email: '', role: 'STAFF' });
  const [sending, setSending] = useState(false);
  const [msg, setMsg] = useState(null);

  const loadData = () => {
    fetch(`${API_BASE}/api/v1/employees`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(data => setEmployees(Array.isArray(data) ? data : [])).catch(() => {});
    fetch(`${API_BASE}/api/v1/invites`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(data => setInvites(Array.isArray(data) ? data : [])).catch(() => {});
    fetch(`${API_BASE}/api/v1/shops`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(data => setShops(Array.isArray(data) ? data.filter(s => s.active !== false) : [])).catch(() => {});
    fetch(`${API_BASE}/api/v1/shops/assignments`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : []).then(data => setShopAssignments(Array.isArray(data) ? data : [])).catch(() => {});
  };

  useEffect(loadData, [token]);

  const sendInvite = async (e) => {
    e.preventDefault(); setSending(true); setMsg(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/invites`, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify(form) });
      if (!res.ok) {
        const data = await res.json();
        if (data.error === 'EMAIL_NOT_VERIFIED') {
          setMsg({ type: 'error', text: t('settings.employees.verifyFirst') });
        } else {
          setMsg({ type: 'error', text: data.error || t('settings.employees.failed') });
        }
        return;
      }
      setMsg({ type: 'success', text: t('settings.employees.inviteSent') }); setForm({ email: '', role: 'STAFF' }); loadData();
    } catch { setMsg({ type: 'error', text: t('settings.networkError') }); } finally { setSending(false); }
  };

  const changeRole = async (empId, newRole) => {
    await fetch(`${API_BASE}/api/v1/employees/${empId}/change-role`, {
      method: 'PATCH', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ role: newRole }),
    }).catch(() => {});
    loadData();
  };

  const toggleShopAssignment = async (userId, shopId) => {
    const exists = shopAssignments.some(a => a.userId === userId && a.shopId === shopId);
    const url = `${API_BASE}/api/v1/shops/${exists ? 'unassign' : 'assign'}`;
    await fetch(url, { method: 'POST', headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` }, body: JSON.stringify({ userId, shopId }) }).catch(() => {});
    loadData();
  };

  const toggleActive = async (empId) => {
    await fetch(`${API_BASE}/api/v1/employees/${empId}/toggle-active`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    loadData();
  };

  const [editingDeskId, setEditingDeskId] = useState(null);
  const [deskDraft, setDeskDraft] = useState('');
  const [deskSavedId, setDeskSavedId] = useState(null);

  const saveDeskLabel = async (empId) => {
    try {
      const res = await fetch(`${API_BASE}/api/v1/employees/${empId}/desk-label`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ deskLabel: deskDraft }),
      });
      if (res.ok) {
        setEditingDeskId(null);
        setDeskSavedId(empId);
        setTimeout(() => setDeskSavedId(null), 2000);
        loadData();
      }
    } catch {}
  };

  return (
    <div>
      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('settings.employees.teamMembers')}</div>
        {employees.length === 0 && <p style={{ color: '#94A3B8', fontSize: '0.85rem' }}>{t('settings.employees.noMembers')}</p>}
        {employees.map((emp) => (
          <div key={emp.id} style={{ ...styles.empRow, opacity: emp.active === false ? 0.5 : 1 }}>
            <span style={{ flex: 1, fontWeight: 600, fontSize: '0.9rem', color: '#F8FAFC', display: 'flex', alignItems: 'center', gap: 8 }}>
              {emp.email}
              {emp.active === false && <span style={{ ...styles.badge('#EF4444'), fontSize: '0.65rem' }}>{t('settings.frozen')}</span>}
            </span>
            {emp.id !== currentUserId && (
              currentUserRole === 'OWNER' && emp.role !== 'ADMIN' ? (
                <select style={{ ...styles.select, minWidth: 100 }} value={emp.role} onChange={(e) => changeRole(emp.id, e.target.value)}>
                  <option value="STAFF">{t('settings.roles.staff')}</option>
                  <option value="MANAGER">{t('settings.roles.manager')}</option>
                  <option value="OWNER">{t('settings.roles.owner')}</option>
                </select>
              ) : currentUserRole === 'MANAGER' && emp.role !== 'OWNER' && emp.role !== 'ADMIN' ? (
                <select style={{ ...styles.select, minWidth: 100 }} value={emp.role} onChange={(e) => changeRole(emp.id, e.target.value)}>
                  <option value="STAFF">{t('settings.roles.staff')}</option>
                  <option value="MANAGER">{t('settings.roles.manager')}</option>
                </select>
              ) : (
                <span style={styles.badge(roleBadgeColor[emp.role] || '#94A3B8')}>{emp.role}</span>
              )
            )}
            {emp.id === currentUserId && <span style={styles.badge(roleBadgeColor[emp.role] || '#94A3B8')}>{emp.role}</span>}
            {emp.role !== 'OWNER' && emp.id !== currentUserId && (
              <button style={emp.active === false ? styles.btnSmallSuccess : styles.btnWarning} onClick={() => toggleActive(emp.id)}>
                {emp.active === false ? t('settings.unfreeze') : t('settings.freeze')}
              </button>
            )}
            {editingDeskId === emp.id ? (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <input
                  style={{ padding: '0.3rem 0.5rem', borderRadius: 4, border: '1.5px solid #334155', background: '#1E293B', color: '#F8FAFC', fontSize: '0.8rem', width: 90 }}
                  value={deskDraft}
                  onChange={(e) => setDeskDraft(e.target.value)}
                  placeholder={t('settings.employees.deskPlaceholder')}
                  autoFocus
                  onKeyDown={(e) => { if (e.key === 'Enter') saveDeskLabel(emp.id); if (e.key === 'Escape') setEditingDeskId(null); }}
                />
                <button style={{ ...styles.btnSmall, padding: '0.3rem 0.5rem', fontSize: '0.75rem' }} onClick={() => saveDeskLabel(emp.id)}>&#10003;</button>
              </span>
            ) : (
              <button
                style={{ ...styles.btnSmall, fontSize: '0.75rem', opacity: emp.defaultDeskLabel ? 1 : 0.6 }}
                onClick={() => { setDeskDraft(emp.defaultDeskLabel || ''); setEditingDeskId(emp.id); }}
                title={t('settings.employees.deskLabel')}
              >
                {emp.defaultDeskLabel || t('settings.employees.deskLabel')}
                {deskSavedId === emp.id && <span style={{ color: '#10B981', marginLeft: 4 }}>&#10003;</span>}
              </button>
            )}
          </div>
        ))}
      </div>
      {shops.length > 0 && employees.filter(e => e.active !== false && e.role !== 'OWNER').length > 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>{t('settings.employees.shopAssignments')}</div>
          <p style={{ fontSize: '0.85rem', color: '#64748B', marginBottom: '1rem' }}>{t('settings.employees.toggleShops')}</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #334155', color: '#94A3B8' }}>{t('settings.employee')}</th>
                  {shops.map(shop => (
                    <th key={shop.id} style={{ textAlign: 'center', padding: '8px', borderBottom: '2px solid #334155', color: '#94A3B8', minWidth: 80 }}>{shop.name}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {employees.filter(e => e.active !== false && e.role !== 'OWNER').map(emp => (
                  <tr key={emp.id}>
                    <td style={{ padding: '8px', borderBottom: '1px solid #334155' }}>
                      <span style={{ fontWeight: 600 }}>{emp.email}</span>
                      <span style={{ ...styles.badge(roleBadgeColor[emp.role]), marginLeft: 8 }}>{emp.role}</span>
                    </td>
                    {shops.map(shop => {
                      const assigned = shopAssignments.some(a => a.userId === emp.id && a.shopId === shop.id);
                      return (
                        <td key={shop.id} style={{ textAlign: 'center', padding: '8px', borderBottom: '1px solid #334155' }}>
                          <input type="checkbox" checked={assigned} onChange={() => toggleShopAssignment(emp.id, shop.id)} style={{ cursor: 'pointer', width: 18, height: 18 }} />
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {currentUserRole === 'OWNER' && <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('settings.employees.inviteEmployee')}</div>
        {msg && <p style={msg.type === 'error' ? styles.error : styles.success}>{msg.text}</p>}
        <form onSubmit={sendInvite} style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <div style={{ flex: 1, minWidth: 180 }}>
            <label style={styles.label}>{t('settings.employees.email')}</label>
            <input style={{ ...styles.input, width: '100%', boxSizing: 'border-box' }} type="email" placeholder={t('settings.employees.emailPlaceholder')} required value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
          </div>
          <div>
            <label style={styles.label}>{t('settings.employees.role')}</label>
            <select style={styles.select} value={form.role} onChange={(e) => setForm((f) => ({ ...f, role: e.target.value }))}>
              <option value="STAFF">{t('settings.roles.staff')}</option>
              <option value="MANAGER">{t('settings.roles.manager')}</option>
            </select>
          </div>
          <button style={styles.btn} type="submit" disabled={sending}>{sending ? t('settings.employees.sending') : t('settings.employees.sendInvite')}</button>
        </form>
      </div>}
      {currentUserRole === 'OWNER' && invites.filter(i => !i.accepted).length > 0 && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>{t('settings.employees.pendingInvites')}</div>
          {invites.filter((i) => !i.accepted).map((inv) => (
            <div key={inv.id} style={{ ...styles.empRow, opacity: inv.revoked ? 0.5 : 1 }}>
              <span style={{ flex: 1, fontSize: '0.9rem', color: '#F8FAFC' }}>{inv.email}</span>
              <span style={styles.badge(roleBadgeColor[inv.role] || '#94A3B8')}>{inv.role}</span>
              <span style={{ fontSize: '0.75rem', color: inv.revoked ? '#EF4444' : '#94A3B8' }}>{inv.revoked ? t('settings.employees.revoked') : t('settings.employees.pending')}</span>
              <button style={inv.revoked ? styles.btnSmallSuccess : styles.btnWarning} onClick={async () => {
                const r = await fetch(`${API_BASE}/api/v1/invites/${inv.id}/toggle-revoked`, { method: 'PATCH', headers: { Authorization: `Bearer ${token}` } });
                if (r.ok) setInvites((prev) => prev.map((i) => i.id === inv.id ? { ...i, revoked: !i.revoked } : i));
              }}>{inv.revoked ? t('settings.employees.restore') : t('settings.employees.revoke')}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Stats Tab ─────────────────────────────────────────────────────────────────

function StatsTab({ token, shops }) {
  const { t } = useTranslation();
  const [stats, setStats] = useState(null);
  const [daily, setDaily] = useState([]);
  const [days, setDays] = useState(7);
  const [shopId, setShopId] = useState('');
  const [services, setServices] = useState([]);
  const [serviceId, setServiceId] = useState('');

  // Fetch services when shop changes
  useEffect(() => {
    if (!shopId) { setServices([]); setServiceId(''); return; }
    fetch(`${API_BASE}/api/v1/services/by-shop/${shopId}`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setServices).catch(() => setServices([]));
    setServiceId('');
  }, [shopId, token]);

  const load = () => {
    const statsUrl = shopId
      ? `${API_BASE}/api/v1/stats/shop/${shopId}?days=${days}`
      : `${API_BASE}/api/v1/stats?days=${days}`;
    fetch(statsUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : null).then(setStats).catch(() => {});

    const dailyUrl = serviceId
      ? `${API_BASE}/api/v1/stats/daily/service/${serviceId}?days=${days}`
      : shopId
        ? `${API_BASE}/api/v1/stats/daily/shop/${shopId}?days=${days}`
        : `${API_BASE}/api/v1/stats/daily?days=${days}`;
    fetch(dailyUrl, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.ok ? r.json() : []).then(setDaily).catch(() => setDaily([]));
  };

  useEffect(load, [token, days, shopId, serviceId]);

  // Pivot daily rows into chart-friendly format: { date, COMPLETED, SKIPPED, OTHER, avgWait }
  const ticketChartData = useMemo(() => {
    const map = {};
    daily.forEach(({ date, outcome, count, avgWait }) => {
      if (!map[date]) map[date] = { date, COMPLETED: 0, SKIPPED: 0, OTHER: 0, avgWait: 0, _waitSum: 0, _waitCount: 0 };
      const bucket = outcome === 'COMPLETED' ? 'COMPLETED' : outcome === 'SKIPPED' ? 'SKIPPED' : 'OTHER';
      map[date][bucket] += count;
      map[date]._waitSum += avgWait * count;
      map[date]._waitCount += count;
    });
    return Object.values(map)
      .map(d => ({ ...d, avgWait: d._waitCount > 0 ? Math.round((d._waitSum / d._waitCount) / 60 * 10) / 10 : 0 }))
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [daily]);

  const chartTooltipStyle = {
    contentStyle: { background: '#1E293B', border: '1px solid #334155', borderRadius: 8, color: '#F8FAFC', fontSize: '0.85rem' },
    labelStyle: { color: '#94A3B8', fontWeight: 600 },
  };

  return (
    <div>
      <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <select style={styles.select} value={shopId} onChange={(e) => setShopId(e.target.value)}>
          <option value="">{t('settings.stats.allShops')}</option>
          {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
        </select>
        {shopId && services.length > 0 && (
          <select style={styles.select} value={serviceId} onChange={(e) => setServiceId(e.target.value)}>
            <option value="">{t('settings.stats.allServices')}</option>
            {services.map(svc => <option key={svc.id} value={svc.id}>{svc.name}</option>)}
          </select>
        )}
        <select style={styles.select} value={days} onChange={(e) => setDays(Number(e.target.value))}>
          <option value={1}>{t('settings.stats.today')}</option>
          <option value={7}>{t('settings.stats.last7')}</option>
          <option value={30}>{t('settings.stats.last30')}</option>
          <option value={90}>{t('settings.stats.last90')}</option>
        </select>
      </div>

      {stats && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: '0.75rem', marginBottom: '1rem' }}>
            <div style={styles.statCard}><div style={styles.statValue}>{stats.totalTickets}</div><div style={styles.statLabel}>{t('settings.stats.totalTickets')}</div></div>
            <div style={styles.statCard}><div style={{ ...styles.statValue, color: '#10B981' }}>{stats.completedTickets}</div><div style={styles.statLabel}>{t('settings.stats.completed')}</div></div>
            <div style={styles.statCard}><div style={{ ...styles.statValue, color: '#F59E0B' }}>{stats.skippedTickets}</div><div style={styles.statLabel}>{t('settings.stats.skipped')}</div></div>
            <div style={styles.statCard}><div style={{ ...styles.statValue, color: '#EF4444' }}>{stats.abandonedTickets}</div><div style={styles.statLabel}>{t('settings.stats.abandoned')}</div></div>
            <div style={styles.statCard}><div style={styles.statValue}>{stats.avgWaitSeconds ? `${Math.round(stats.avgWaitSeconds / 60)}m` : '-'}</div><div style={styles.statLabel}>{t('settings.stats.avgWait')}</div></div>
          </div>

          {stats.staffBreakdown?.length > 0 && (
            <div style={styles.card}>
              <div style={styles.sectionTitle}>{t('settings.stats.staffPerformance')}</div>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: '8px', borderBottom: '2px solid #334155', color: '#94A3B8' }}>{t('settings.employee')}</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #334155', color: '#94A3B8' }}>{t('settings.stats.tickets')}</th>
                    <th style={{ textAlign: 'right', padding: '8px', borderBottom: '2px solid #334155', color: '#94A3B8' }}>{t('settings.stats.avgWait')}</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.staffBreakdown.map(s => (
                    <tr key={s.userId}>
                      <td style={{ padding: '8px', borderBottom: '1px solid #334155', fontWeight: 600 }}>{s.email}</td>
                      <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #334155' }}>{s.ticketsProcessed}</td>
                      <td style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #334155' }}>{s.avgWaitSeconds ? `${Math.round(s.avgWaitSeconds / 60)}m` : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {ticketChartData.length > 0 && (
        <>
          <div style={styles.card}>
            <div style={styles.sectionTitle}>{t('settings.stats.dailyTickets')}</div>
            <ResponsiveContainer width="100%" height={300}>
              <AreaChart data={ticketChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <YAxis allowDecimals={false} tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <Tooltip {...chartTooltipStyle} />
                <Legend wrapperStyle={{ color: '#94A3B8', fontSize: '0.85rem' }} />
                <Area type="monotone" dataKey="COMPLETED" stackId="1" stroke="#10B981" fill="#10B981" fillOpacity={0.6} name={t('settings.stats.completed')} />
                <Area type="monotone" dataKey="SKIPPED" stackId="1" stroke="#F59E0B" fill="#F59E0B" fillOpacity={0.6} name={t('settings.stats.skipped')} />
                <Area type="monotone" dataKey="OTHER" stackId="1" stroke="#EF4444" fill="#EF4444" fillOpacity={0.6} name={t('settings.stats.cancelledAbandoned')} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div style={styles.card}>
            <div style={styles.sectionTitle}>{t('settings.stats.avgWaitTime')}</div>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={ticketChartData} margin={{ top: 5, right: 20, bottom: 5, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <YAxis tick={{ fill: '#94A3B8', fontSize: 12 }} tickLine={{ stroke: '#334155' }} />
                <Tooltip {...chartTooltipStyle} />
                <Line type="monotone" dataKey="avgWait" stroke="#06B6D4" strokeWidth={2} dot={{ fill: '#06B6D4', r: 3 }} name={t('settings.stats.avgWaitMin')} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </>
      )}

      {!stats && <p style={{ color: '#94A3B8', textAlign: 'center' }}>{t('settings.stats.loadingStats')}</p>}
    </div>
  );
}

// ── Kiosk Tab ────────────────────────────────────────────────────────────────

function KioskTab({ shopId, token, shops, onShopsChange }) {
  const { t } = useTranslation();
  const { canUseDynamicQr } = usePlan();
  const [kioskHeadline, setKioskHeadline] = useState('');
  const [calledTitle, setCalledTitle] = useState('');
  const [calledBody, setCalledBody] = useState('');
  const [kioskSubtext, setKioskSubtext] = useState('');
  const [dynamicQrEnabled, setDynamicQrEnabled] = useState(false);
  const [dynamicQrMsg, setDynamicQrMsg] = useState(null); // error surface for the toggle
  const [walkUpTicketEnabled, setWalkUpTicketEnabled] = useState(true);
  const [walkUpOverlayDurationSeconds, setWalkUpOverlayDurationSeconds] = useState(45);
  const [walkUpDisplayName, setWalkUpDisplayName] = useState('');
  const [ttsEnabled, setTtsEnabled] = useState(true);
  const [reviewPromptEnabled, setReviewPromptEnabled] = useState(false);
  const [reviewUrl, setReviewUrl] = useState('');
  const [reviewThreshold, setReviewThreshold] = useState(4);
  const [textSaving, setTextSaving] = useState(false);
  const [textMsg, setTextMsg] = useState(null);
  const [pairing, setPairing] = useState(null); // { code, expiresInSeconds } after minting
  const [pairingBusy, setPairingBusy] = useState(false);
  const [pairingErr, setPairingErr] = useState(null);

  const generatePairingCode = async () => {
    setPairingBusy(true);
    setPairingErr(null);
    try {
      const res = await fetch(`${API_BASE}/api/v1/shops/${shopId}/kiosk-devices/pairing-code`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('mint failed');
      setPairing(await res.json());
    } catch {
      setPairingErr(t('settings.kiosk.pairError', 'Could not generate a pairing code'));
    } finally {
      setPairingBusy(false);
    }
  };

  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  // Load kiosk text from shop data
  useEffect(() => {
    const shop = shops?.find((s) => s.id === shopId);
    if (shop) {
      setKioskHeadline(shop.kioskHeadline || '');
      setKioskSubtext(shop.kioskSubtext || '');
      setCalledTitle(shop.calledTitle || '');
      setDynamicQrEnabled(!!shop.dynamicQrEnabled);
      setWalkUpTicketEnabled(shop.walkUpTicketEnabled !== false);
      setWalkUpDisplayName(shop.walkUpDisplayName || '');
      setWalkUpOverlayDurationSeconds(
        typeof shop.walkUpOverlayDurationSeconds === 'number' && shop.walkUpOverlayDurationSeconds > 0
          ? shop.walkUpOverlayDurationSeconds
          : 45
      );
      setTtsEnabled(shop.ttsEnabled !== false);
      setReviewPromptEnabled(!!shop.reviewPromptEnabled);
      setReviewUrl(shop.reviewUrl || '');
      if (typeof shop.reviewThreshold === 'number' && shop.reviewThreshold >= 1 && shop.reviewThreshold <= 5) {
        setReviewThreshold(shop.reviewThreshold);
      }
      setCalledBody(shop.calledBody || '');
    }
    setTextMsg(null);
  }, [shopId, shops]);

  const saveText = async (e) => {
    e.preventDefault();
    setTextSaving(true);
    setTextMsg(null);
    try {
      const shop = shops?.find((s) => s.id === shopId);
      const res = await fetch(`${API_BASE}/api/v1/shops/${shopId}`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ name: shop?.name || '', description: shop?.description || '', timezone: shop?.timezone || 'UTC', kioskHeadline, kioskSubtext, calledTitle, calledBody, dynamicQrEnabled, walkUpTicketEnabled, walkUpOverlayDurationSeconds, walkUpDisplayName: walkUpDisplayName.trim() || null, ttsEnabled, reviewPromptEnabled, reviewUrl: reviewUrl.trim(), reviewThreshold }),
      });
      if (res.ok) {
        setTextMsg({ type: 'success', text: t('settings.kiosk.textSaved') });
        if (onShopsChange) onShopsChange();
      } else {
        setTextMsg({ type: 'error', text: t('settings.networkError') });
      }
    } catch {
      setTextMsg({ type: 'error', text: t('settings.networkError') });
    } finally {
      setTextSaving(false);
    }
  };

  const kioskUrl = `${window.location.origin}/kiosk/${shopId}`;
  const displayUrl = `${window.location.origin}/display/${shopId}`;
  // NOTE: We deliberately do NOT append the admin access token to these
  // preview URLs. A JWT in a URL leaks into the tablet's address bar and
  // browser history, nginx access logs, and any analytics/Sentry event URL.
  // Opening a preview in the signed-in admin's own browser auto-registers
  // from the existing session (a new tab inherits a copy of session/local
  // storage). Pairing a separate, un-signed-in tablet must use a proper
  // server-issued pairing flow — never a bearer token in a query string.

  return (
    <div>
      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.dynamicQrTitle')}</h3>
        <p style={{ fontSize: '0.85rem', color: '#94A3B8', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          {t('settings.kiosk.dynamicQrHint')}
        </p>
        <label style={{
          display: 'flex', alignItems: 'center', gap: 10,
          cursor: canUseDynamicQr ? 'pointer' : 'not-allowed',
          fontSize: '0.9rem', color: '#F8FAFC', opacity: canUseDynamicQr ? 1 : 0.6,
        }}>
          <input
            type="checkbox"
            checked={dynamicQrEnabled}
            disabled={!canUseDynamicQr}
            onChange={async (e) => {
              const val = e.target.checked;
              setDynamicQrMsg(null);
              setDynamicQrEnabled(val);
              // Dedicated single-field endpoint — the general PUT /shops/:id
              // wipes nullable text fields when the body omits them, which
              // silently deletes kiosk headline/subtext/review URL. See
              // ShopController#setDynamicQr.
              try {
                const res = await fetch(`${API_BASE}/api/v1/shops/${shopId}/dynamic-qr`, {
                  method: 'POST', headers,
                  body: JSON.stringify({ enabled: val }),
                });
                if (res.ok) {
                  if (onShopsChange) onShopsChange();
                  return;
                }
                setDynamicQrEnabled(!val); // revert optimistic toggle
                const data = await res.json().catch(() => ({}));
                setDynamicQrMsg({
                  type: 'error',
                  text: data.error === 'FEATURE_NOT_AVAILABLE'
                    ? t('plan.featureLocked.dynamicQr')
                    : t('settings.kiosk.dynamicQrFailed'),
                });
              } catch {
                setDynamicQrEnabled(!val);
                setDynamicQrMsg({ type: 'error', text: t('settings.networkError') });
              }
            }}
            style={{ width: 18, height: 18, accentColor: '#7C3AED' }}
          />
          {t('settings.kiosk.dynamicQrLabel')}
        </label>
        {!canUseDynamicQr && (
          <p style={{ fontSize: '0.8rem', color: '#FBBF24', margin: '0.5rem 0 0', lineHeight: 1.4 }}>
            {t('plan.featureLocked.dynamicQr')}
          </p>
        )}
        {dynamicQrMsg && (
          <p style={{ ...(dynamicQrMsg.type === 'error' ? styles.error : styles.success), margin: '0.5rem 0 0' }}>
            {dynamicQrMsg.text}
          </p>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.walkUpTitle')}</h3>
        <p style={{ fontSize: '0.85rem', color: '#94A3B8', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          {t('settings.kiosk.walkUpHint')}
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', color: '#F8FAFC' }}>
          <input
            type="checkbox"
            checked={walkUpTicketEnabled}
            onChange={(e) => setWalkUpTicketEnabled(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: '#7C3AED' }}
          />
          {t('settings.kiosk.walkUpLabel')}
        </label>
        {walkUpTicketEnabled && (
          <div style={{ marginTop: '0.75rem' }}>
            <label style={styles.label}>{t('settings.kiosk.walkUpDisplayName')}</label>
            <input
              type="text"
              maxLength={100}
              value={walkUpDisplayName}
              onChange={(e) => setWalkUpDisplayName(e.target.value)}
              placeholder={t('settings.kiosk.walkUpDisplayNamePlaceholder')}
              style={{ ...styles.inputFull, maxWidth: 300 }}
            />
            <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0.25rem 0 0.75rem' }}>
              {t('settings.kiosk.walkUpDisplayNameHint')}
            </p>
            <label style={styles.label}>{t('settings.kiosk.walkUpDuration')}</label>
            <input
              type="number"
              min={5}
              max={300}
              value={walkUpOverlayDurationSeconds}
              onChange={(e) => {
                const v = parseInt(e.target.value, 10);
                if (!Number.isNaN(v)) setWalkUpOverlayDurationSeconds(Math.min(300, Math.max(5, v)));
              }}
              style={{ ...styles.inputFull, maxWidth: 120 }}
            />
            <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0.25rem 0 0' }}>
              {t('settings.kiosk.walkUpDurationHint')}
            </p>
          </div>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.ttsTitle')}</h3>
        <p style={{ fontSize: '0.85rem', color: '#94A3B8', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          {t('settings.kiosk.ttsHint')}
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', color: '#F8FAFC' }}>
          <input
            type="checkbox"
            checked={ttsEnabled}
            onChange={(e) => setTtsEnabled(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: '#7C3AED' }}
          />
          {t('settings.kiosk.ttsLabel')}
        </label>
      </div>

      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.reviewTitle')}</h3>
        <p style={{ fontSize: '0.85rem', color: '#94A3B8', margin: '0 0 0.75rem', lineHeight: 1.5 }}>
          {t('settings.kiosk.reviewHint')}
        </p>
        <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: '0.9rem', color: '#F8FAFC' }}>
          <input
            type="checkbox"
            checked={reviewPromptEnabled}
            onChange={(e) => setReviewPromptEnabled(e.target.checked)}
            style={{ width: 18, height: 18, accentColor: '#7C3AED' }}
          />
          {t('settings.kiosk.reviewLabel')}
        </label>
        {reviewPromptEnabled && (
          <div style={{ marginTop: '0.75rem' }}>
            <label style={styles.label}>{t('settings.kiosk.reviewUrl')}</label>
            <input
              type="url"
              style={styles.inputFull}
              placeholder={t('settings.kiosk.reviewUrlPlaceholder')}
              value={reviewUrl}
              onChange={(e) => setReviewUrl(e.target.value)}
              maxLength={2000}
            />
            <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0.25rem 0 0.75rem' }}>
              {t('settings.kiosk.reviewUrlHint')}
            </p>
            <label style={styles.label}>{t('settings.kiosk.reviewThreshold')}</label>
            <select
              value={reviewThreshold}
              onChange={(e) => setReviewThreshold(parseInt(e.target.value, 10))}
              style={{ ...styles.inputFull, maxWidth: 240 }}
            >
              <option value={3}>{t('settings.kiosk.reviewThresholdOption3')}</option>
              <option value={4}>{t('settings.kiosk.reviewThresholdOption4')}</option>
              <option value={5}>{t('settings.kiosk.reviewThresholdOption5')}</option>
            </select>
            <p style={{ fontSize: '0.8rem', color: '#64748B', margin: '0.25rem 0 0' }}>
              {t('settings.kiosk.reviewThresholdHint')}
            </p>
          </div>
        )}
      </div>

      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.customText')}</h3>
        {textMsg && <p style={textMsg.type === 'error' ? styles.error : styles.success}>{textMsg.text}</p>}
        <form onSubmit={saveText}>
          <label style={styles.label}>{t('settings.kiosk.headline')}</label>
          <input
            style={styles.inputFull}
            placeholder={t('settings.kiosk.headlinePlaceholder')}
            value={kioskHeadline}
            onChange={(e) => setKioskHeadline(e.target.value)}
          />
          <label style={styles.label}>{t('settings.kiosk.subtext')}</label>
          <input
            style={styles.inputFull}
            placeholder={t('settings.kiosk.subtextPlaceholder')}
            value={kioskSubtext}
            onChange={(e) => setKioskSubtext(e.target.value)}
          />
          <h3 style={{ margin: '1.25rem 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.calledTextTitle')}</h3>
          <label style={styles.label}>{t('settings.kiosk.calledTitle')}</label>
          <input
            style={styles.inputFull}
            placeholder={t('settings.kiosk.calledTitlePlaceholder')}
            value={calledTitle}
            onChange={(e) => setCalledTitle(e.target.value)}
          />
          <label style={styles.label}>{t('settings.kiosk.calledBodyLabel')}</label>
          <input
            style={styles.inputFull}
            placeholder={t('settings.kiosk.calledBodyPlaceholder')}
            value={calledBody}
            onChange={(e) => setCalledBody(e.target.value)}
          />
          <button style={styles.btn} type="submit" disabled={textSaving}>
            {textSaving ? t('settings.saving') : t('settings.kiosk.saveText')}
          </button>
        </form>
      </div>

      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>
          {t('settings.kiosk.pairTitle', 'Pair a new device')}
        </h3>
        <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0 0 0.75rem' }}>
          {t('settings.kiosk.pairHint', 'Generate a one-time code, then enter it on the tablet or TV to pair it securely — no need to sign in on that device.')}
        </p>
        {pairing ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: 'monospace', fontSize: '1.6rem', fontWeight: 800, letterSpacing: '0.2em',
              color: '#F8FAFC', background: '#020617', padding: '0.4rem 0.9rem', borderRadius: 8,
            }}>{pairing.code}</span>
            <span style={{ fontSize: '0.8rem', color: '#94A3B8' }}>
              {t('settings.kiosk.pairExpiry', 'Valid for {{min}} minutes — single use', { min: Math.round((pairing.expiresInSeconds || 300) / 60) })}
            </span>
            <button style={styles.btnOutline} onClick={generatePairingCode} disabled={pairingBusy}>
              {t('settings.kiosk.pairRegenerate', 'New code')}
            </button>
          </div>
        ) : (
          <button style={styles.btn} onClick={generatePairingCode} disabled={pairingBusy}>
            {pairingBusy ? '…' : t('settings.kiosk.pairGenerate', 'Generate pairing code')}
          </button>
        )}
        {pairingErr && <p style={{ color: '#F87171', fontSize: '0.8rem', marginTop: 8 }}>{pairingErr}</p>}
      </div>

      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.displayUrl')}</h3>
        <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0 0 0.75rem' }}>
          {t('settings.kiosk.displayDescription')}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input style={{ ...styles.inputFull, flex: 1, marginBottom: 0 }} value={kioskUrl} readOnly />
          <CopyButton text={kioskUrl} style={styles.btn} label={t('settings.qr.copy')} />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <a
            href={`${kioskUrl}?orientation=landscape`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...styles.btn, textDecoration: 'none', display: 'inline-block', textAlign: 'center', flex: 1 }}
          >
            {t('settings.kiosk.previewLandscape')}
          </a>
          <a
            href={`${kioskUrl}?orientation=portrait`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...styles.btnOutline, textDecoration: 'none', display: 'inline-block', textAlign: 'center', flex: 1 }}
          >
            {t('settings.kiosk.previewPortrait')}
          </a>
        </div>
      </div>

      <div style={styles.card}>
        <h3 style={{ margin: '0 0 0.5rem', fontSize: '1rem', fontWeight: 700, color: '#F8FAFC' }}>{t('settings.kiosk.displayScreenUrl')}</h3>
        <p style={{ fontSize: '0.85rem', color: '#64748B', margin: '0 0 0.75rem' }}>
          {t('settings.kiosk.displayScreenDescription')}
        </p>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <input style={{ ...styles.inputFull, flex: 1, marginBottom: 0 }} value={displayUrl} readOnly />
          <CopyButton text={displayUrl} style={styles.btn} label={t('settings.qr.copy')} />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
          <a
            href={`${displayUrl}?orientation=landscape`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...styles.btn, textDecoration: 'none', display: 'inline-block', textAlign: 'center', flex: 1 }}
          >
            {t('settings.kiosk.previewLandscape')}
          </a>
          <a
            href={`${displayUrl}?orientation=portrait`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ ...styles.btnOutline, textDecoration: 'none', display: 'inline-block', textAlign: 'center', flex: 1 }}
          >
            {t('settings.kiosk.previewPortrait')}
          </a>
        </div>
      </div>

    </div>
  );
}

// ── Account Tab ──────────────────────────────────────────────────────────────

function AccountTab({ token }) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [pwForm, setPwForm] = useState({ currentPassword: '', newPassword: '', confirm: '' });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState(null);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    setMsg(null);
    if (pwForm.newPassword.length < 8) { setMsg({ type: 'error', text: t('account.minLength') }); return; }
    if (pwForm.newPassword !== pwForm.confirm) { setMsg({ type: 'error', text: t('account.mismatch') }); return; }
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/v1/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ currentPassword: pwForm.currentPassword, newPassword: pwForm.newPassword }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        setMsg({ type: 'error', text: data.message || t('account.changeFailed') });
        return;
      }
      setMsg({ type: 'success', text: t('account.changeSuccess') });
      setPwForm({ currentPassword: '', newPassword: '', confirm: '' });
    } catch {
      setMsg({ type: 'error', text: t('settings.networkError') });
    } finally { setSaving(false); }
  };

  return (
    <div>
      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('account.profile')}</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
          <div style={{ width: 48, height: 48, borderRadius: '50%', background: '#334155', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', color: '#94A3B8', fontWeight: 700 }}>
            {(user?.email || '?')[0].toUpperCase()}
          </div>
          <div>
            <div style={{ fontWeight: 600, color: '#F8FAFC', fontSize: '0.95rem' }}>{user?.email}</div>
            <div style={{ fontSize: '0.8rem', color: '#94A3B8' }}>{user?.role}</div>
          </div>
        </div>
      </div>

      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('account.changePassword')}</div>
        {msg && <p style={msg.type === 'error' ? styles.error : styles.success}>{msg.text}</p>}
        <form onSubmit={handleChangePassword}>
          <label style={styles.label}>{t('account.currentPassword')}</label>
          <input style={styles.inputFull} type="password" value={pwForm.currentPassword} onChange={(e) => setPwForm(f => ({ ...f, currentPassword: e.target.value }))} required />
          <label style={styles.label}>{t('account.newPassword')}</label>
          <input style={styles.inputFull} type="password" placeholder={t('account.minLengthPlaceholder')} value={pwForm.newPassword} onChange={(e) => setPwForm(f => ({ ...f, newPassword: e.target.value }))} required minLength={8} />
          <label style={styles.label}>{t('account.confirmPassword')}</label>
          <input style={styles.inputFull} type="password" value={pwForm.confirm} onChange={(e) => setPwForm(f => ({ ...f, confirm: e.target.value }))} required />
          <button style={styles.btn} type="submit" disabled={saving}>{saving ? t('settings.saving') : t('account.changePasswordBtn')}</button>
        </form>
      </div>

      {user?.role === 'OWNER' && (
        <div style={styles.card}>
          <div style={styles.sectionTitle}>{t('account.plan')}</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
            <span style={{ display: 'inline-block', padding: '4px 12px', borderRadius: 6, fontSize: '0.85rem', fontWeight: 700, background: 'linear-gradient(135deg, #7C3AED, #06B6D4)', color: '#fff' }}>{t(`plan.${(user?.plan || 'SOLO').toLowerCase()}`)}</span>
            <span style={{ fontSize: '0.85rem', color: '#94A3B8' }}>{t('account.currentPlan')}</span>
          </div>
          {user?.plan !== 'CHAIN' && user?.plan !== 'INTERNAL' && (
            <p style={{ fontSize: '0.85rem', color: '#94A3B8', margin: '0 0 1rem' }}>{t('account.upgradeHint')}</p>
          )}
          <button style={{ ...styles.btnOutline, opacity: 0.6, cursor: 'not-allowed' }} disabled>{t('account.manageBilling')}</button>
        </div>
      )}

      <div style={styles.card}>
        <div style={styles.sectionTitle}>{t('account.legalDocs')}</div>
        <p style={{ fontSize: '0.85rem', color: '#94A3B8', margin: '0 0 0.75rem' }}>{t('account.legalDocsHint')}</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
          <a href="/legal/dpa" target="_blank" rel="noopener noreferrer" style={{ ...styles.btnOutline, textDecoration: 'none', display: 'inline-block' }}>{t('account.legalDpa')}</a>
          <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ ...styles.btnOutline, textDecoration: 'none', display: 'inline-block' }}>{t('account.legalPrivacy')}</a>
          <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ ...styles.btnOutline, textDecoration: 'none', display: 'inline-block' }}>{t('account.legalTerms')}</a>
          <a href="/imprint" target="_blank" rel="noopener noreferrer" style={{ ...styles.btnOutline, textDecoration: 'none', display: 'inline-block' }}>{t('account.legalImprint')}</a>
        </div>
      </div>
    </div>
  );
}

// ── Settings Page ─────────────────────────────────────────────────────────────

export default function SettingsPage() {
  const { t } = useTranslation();
  const { token, user } = useAuth();
  const location = useLocation();
  const isOwner = user?.role === 'OWNER';
  const isManager = user?.role === 'MANAGER';
  const tabFromQuery = new URLSearchParams(location.search).get('tab');
  const defaultTab = location.state?.tab || tabFromQuery || (isOwner ? 'shops' : 'hours');
  const [tab, setTab] = useState(defaultTab);
  const [shops, setShops] = useState([]);
  const [selectedShopId, setSelectedShopId] = useState('');

  const loadShops = () => {
    fetch(`${API_BASE}/api/v1/shops`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.ok ? r.json() : [])
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setShops(list);
        setSelectedShopId((prev) => {
          if (list.find((s) => s.id === prev)) return prev;
          return list.length > 0 ? list[0].id : '';
        });
      })
      .catch(() => {});
  };

  useEffect(loadShops, [token]);

  // Update tab if navigation state or ?tab= query param changes
  useEffect(() => {
    if (location.state?.tab) { setTab(location.state.tab); return; }
    const q = new URLSearchParams(location.search).get('tab');
    if (q) setTab(q);
  }, [location.state, location.search]);

  const needsShop = tab === 'hours' || tab === 'qr';

  return (
    <div>
      <main style={styles.main}>

        {needsShop && shops.filter(s => s.active !== false).length > 0 && (
          <select style={{ ...styles.select, width: '100%', marginBottom: '1rem' }} value={selectedShopId} onChange={(e) => setSelectedShopId(e.target.value)}>
            {shops.filter(s => s.active !== false).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        )}

        {needsShop && shops.filter(s => s.active !== false).length === 0 && (
          <div style={styles.card}><p style={{ color: '#94A3B8', fontSize: '0.9rem', textAlign: 'center', margin: 0 }}>{t('settings.createShopFirst')}</p></div>
        )}

        {tab === 'shops' && <ShopsTab token={token} shops={shops} onShopsChange={loadShops} />}
        {tab === 'services' && <ServicesTab token={token} shops={shops} />}
        {tab === 'hours' && selectedShopId && <BusinessHoursTab shopId={selectedShopId} token={token} shopTimezone={shops.find(s => s.id === selectedShopId)?.timezone} />}
        {tab === 'qr' && selectedShopId && (
          <>
            <QrCodeTab shopId={selectedShopId} token={token} shops={shops} />
            <KioskTab shopId={selectedShopId} token={token} shops={shops} onShopsChange={loadShops} />
          </>
        )}
        {tab === 'employees' && <EmployeesTab token={token} currentUserId={user?.userId} currentUserRole={user?.role} />}
        {tab === 'stats' && <StatsTab token={token} shops={shops} />}
        {tab === 'referrals' && <ReferralsTab token={token} isOwner={isOwner} />}
        {tab === 'billing' && <BillingTab token={token} isOwner={isOwner} />}
        {tab === 'account' && <AccountTab token={token} />}
      </main>
    </div>
  );
}
